// src/components/Feed/AssignTasksDialog.js
// @flow

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { log } from '../../utils/jsUtils';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

import Downshift from 'downshift';
import assignTasksDialogStyles from './Styles/AssignTasksDialog.Style';
import { useTheme } from '@mui/material/styles';

import { useI18n } from '../../contexts/I18nContext';
import useAuthenticatedBackend from '../../hooks/useAuthenticatedBackend';

import type { Task } from '../../services/SPBackend';

type SuggestedListProps = {
  inputValue: string,
  backend: Object,
  getItemProps: Function
};
const SuggestedList = (props: SuggestedListProps) => {
  const { inputValue, backend, getItemProps } = props;
  const value = inputValue ? inputValue.trim().toLowerCase() : '';

  const [users, setUsers] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(true);

  useEffect(() => {
    if (backend) {
      setIsStaffLoading(true);
      backend.findStaffMembers(value)
        .then((response) => {
          setUsers(response.data);
          setIsStaffLoading(false);
        })
        .catch((err) => {
          log('[SuggestedList] findStaffMembers error:', err);
          setIsStaffLoading(false);
        });
    } else {
      setUsers([]);
      setIsStaffLoading(false);
    }
  }, [inputValue, backend]);

  if (!users || users.length === 0) {
    return (
      <MenuItem component="div" key="no-staff">
        {isStaffLoading ? 'Loading...' : 'No staff found.'}
      </MenuItem>
    );
  }
  return users.map((u) => (
    <MenuItem
      {...getItemProps({ item: u })}
      component="div"
      key={u._id}
    >
      {`${u.FirstName} ${u.LastName}`}
    </MenuItem>
  ));
};

type Props = {
  open: boolean,
  setOpen: (val: boolean) => void,
  triggerAction: Function,
  tasks: Array<Task>
};

const AssignTasksDialog = (props: Props) => {
  const { open, setOpen, triggerAction, tasks } = props;
  const user = useSelector(state => state.user.user);
  const backend = useAuthenticatedBackend();
  const i18n = useI18n();
  const theme = useTheme();

  const [assignedStaff, setAssignedStaff] = useState(null);
  const [initialAssignedStaff, setInitialAssignedStaff] = useState(null);
  const [selectedAutocompleteVal, setSelectedAutocompleteVal] = useState(null);

  useEffect(() => {
    if (tasks && tasks.length > 0) {
      let assignedUser = null;
      tasks.forEach((task) => {
        if (task && task.Assignee && (assignedUser === null || assignedUser._id === task.Assignee._id)) {
          assignedUser = task.Assignee;
        } else {
          assignedUser = null;
        }
      });
      setInitialAssignedStaff(assignedUser);
    }
  }, [tasks]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setOpen(false);
  };

  if (!tasks || tasks.length === 0) return null;

  return (
    <Dialog
      aria-labelledby="form-assign-task"
      onClose={handleClose}
      open={open}
    >
      <form
        autoComplete="off"
        noValidate
        style={assignTasksDialogStyles.dialogFormCtr(theme)}
        className="normal"
        onSubmit={onSubmit}
      >
        <DialogTitle id="form-dialog-title">
          {tasks.length > 1
            ? `Assign these ${tasks.length} ${i18n.t('tasks')} to...`
            : `Assign this ${i18n.t('task')} to...`
          }
        </DialogTitle>

        <DialogContent
          sx={{
            ...assignTasksDialogStyles.grow(theme),
            ...assignTasksDialogStyles.flexContainer(theme)
          }}
        >
          <div id="searchTenant">
            {initialAssignedStaff && (
              <p>
                <b>{`${initialAssignedStaff.FirstName} ${initialAssignedStaff.LastName}`}</b>
                {' is assigned to '}
                {tasks.length > 1
                  ? `these ${tasks.length} ${i18n.t('tasks')}.`
                  : `this ${i18n.t('task')}.`
                }
                <br /><br />{'Reassign to:'}
              </p>
            )}

            <Downshift
              id="downshift-simple"
              itemToString={(item) => (item ? `${item.FirstName} ${item.LastName}` : '')}
              onSelect={(selectedStaff) => setAssignedStaff(selectedStaff)}
            >
              {({
                getInputProps,
                getItemProps,
                getMenuProps,
                inputValue,
                isOpen,
                openMenu
              }) => (
                <div style={assignTasksDialogStyles.container(theme)}>
                  <TextField
                    fullWidth
                    placeholder="Search a staff member"
                    {...getInputProps()}
                    onFocus={openMenu}
                  />
                  <div {...getMenuProps()}>
                    {isOpen && (
                      <Paper
                        style={assignTasksDialogStyles.paper(theme)}
                        square
                      >
                        <SuggestedList
                          getItemProps={getItemProps}
                          inputValue={inputValue}
                          backend={backend}
                        />
                      </Paper>
                    )}
                  </div>
                </div>
              )}
            </Downshift>
          </div>
        </DialogContent>

        <DialogActions>
          <Button color="primary" onClick={handleClose}>
            {'Cancel'}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              handleClose();
              triggerAction(tasks, assignedStaff, user);
            }}
          >
            {'Assign'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignTasksDialog;